import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, X, Buildings, HouseLine, Globe, ArrowSquareOut,
    Pause, Play, Archive, Lightning, CheckCircle, ClockClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";

const PRIORITIES = ["low", "medium", "high", "urgent"];

const label = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return dt; } };
const fmt = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return dt; } };

const STATUS_VARIANT = { ACTIVE: "success", PAUSED: "warn", ARCHIVED: "neutral" };
const DUE_VARIANT = { overdue: "danger", due: "warn", due_soon: "warn", upcoming: "neutral", unscheduled: "neutral", in_progress: "default", paused: "neutral", archived: "neutral" };

function dueLabel(p) {
    const s = p.due_state;
    if (s === "overdue") return `Overdue ${p.overdue_days}d`;
    if (s === "due") return "Due today";
    if (s === "due_soon") return "Due soon";
    if (s === "upcoming") return `Upcoming · ${fmtDate(p.next_due_date)}`;
    if (s === "unscheduled") return "Unscheduled";
    if (s === "in_progress") return "Awaiting Completion";
    return label(s);
}

function financialLabel(h) {
    if (!h.financial_status) return null;
    if (h.financial_status !== "POSTED") return "Financial: Not Posted";
    const kind = h.financial_transaction_type === "INVOICE" ? "Flat Invoice" : "Society Expense";
    const statusPart = h.financial_transaction_type === "INVOICE" ? label(h.financial_invoice_status || "") : "Posted";
    return `Financial: ${kind}${statusPart ? ` — ${statusPart}` : ""} (${h.financial_transaction_number || ""})`;
}

function emptyForm() {
    return {
        name: "", category: "", description: "", target_type: "GENERAL",
        common_area: "", common_area_location: "", target_block_id: "", target_flat_id: "",
        frequency: "monthly", custom_interval_days: "", start_date: new Date().toISOString().slice(0, 10),
        default_provider_id: "", priority: "medium", estimated_cost: "",
    };
}

// ---------- Plan detail dialog ----------
function PlanDetail({ id, onClose, onChange }) {
    const [plan, setPlan] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [p, h] = await Promise.all([
                api.get(`/preventive-maintenance/${id}`),
                api.get(`/preventive-maintenance/${id}/history`),
            ]);
            setPlan(p.data); setHistory(h.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function pause() {
        try { await api.post(`/preventive-maintenance/${id}/pause`); toast.success("Plan paused"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function resume() {
        try { await api.post(`/preventive-maintenance/${id}/resume`); toast.success("Plan resumed"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function archive() {
        try { await api.post(`/preventive-maintenance/${id}/archive`); toast.success("Plan archived"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }

    if (loading || !plan) return <LoadingState message="Loading plan..." />;

    return (
        <div className="space-y-6" data-testid="pm-detail-dialog">
            <div className="border-b border-brand-line pb-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label(plan.category)}</p>
                        <h2 className="font-heading text-xl md:text-2xl tracking-tight text-brand-ink mt-1" data-testid="pm-detail-name">{plan.name}</h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Chip variant={STATUS_VARIANT[plan.status] || "default"} data-testid="pm-detail-status">{label(plan.status)}</Chip>
                        <Chip variant={DUE_VARIANT[plan.due_state]}>{dueLabel(plan)}</Chip>
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Frequency</span><span className="font-medium text-brand-ink">{plan.frequency === "custom" ? `Every ${plan.custom_interval_days}d` : label(plan.frequency)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Target</span><span className="font-medium text-brand-ink">{plan.target_type === "COMMON_AREA" ? <><Buildings size={11} weight="duotone" className="inline mr-1" />{plan.common_area}</> : plan.target_type === "RESIDENT" ? <><HouseLine size={11} weight="duotone" className="inline mr-1" />{plan.target_flat_label} · {plan.target_resident_name || "—"}</> : <><Globe size={11} weight="duotone" className="inline mr-1" />General / Society-wide</>}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Priority</span><span className="font-medium text-brand-ink">{label(plan.priority)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Provider</span><span className="font-medium text-brand-ink">{plan.default_provider_name || "Unassigned"}</span></div>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Start date</span><span className="font-medium text-brand-ink">{fmtDate(plan.start_date)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Next due</span><span className="font-medium text-brand-ink">{fmtDate(plan.next_due_date)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Last completed</span><span className="font-medium text-brand-ink">{fmtDate(plan.last_completed_date)}</span></div>
                    {plan.estimated_cost != null && <div className="flex justify-between"><span className="text-brand-inkSoft">Est. cost</span><span className="font-medium text-brand-ink">₹{plan.estimated_cost.toLocaleString("en-IN")}</span></div>}
                </div>
            </div>

            {plan.description && (
                <div className="bg-brand-bg border border-brand-line rounded-sm p-4">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Description</p>
                    <p className="text-sm text-brand-ink whitespace-pre-wrap">{plan.description}</p>
                </div>
            )}

            {plan.occurrence_summary && (
                <div className="grid grid-cols-3 gap-3" data-testid="pm-detail-occurrence-summary">
                    <StatCard label="Total" value={plan.occurrence_summary.total} testId="pm-detail-occ-total" />
                    <StatCard label="Completed" value={plan.occurrence_summary.completed} testId="pm-detail-occ-completed" />
                    <StatCard label="In Progress" value={plan.occurrence_summary.in_progress} testId="pm-detail-occ-in-progress" />
                </div>
            )}

            {plan.status !== "ARCHIVED" && (
                <div className="bg-white border border-brand-line rounded-sm p-4 flex flex-wrap gap-2" data-testid="pm-detail-actions">
                    {plan.status === "ACTIVE" ? (
                        <Button data-testid="pm-detail-pause-btn" onClick={pause} variant="outline" className="rounded-full">
                            <Pause size={14} className="mr-1.5" weight="duotone" /> Pause
                        </Button>
                    ) : (
                        <Button data-testid="pm-detail-resume-btn" onClick={resume} variant="outline" className="rounded-full">
                            <Play size={14} className="mr-1.5" weight="duotone" /> Resume
                        </Button>
                    )}
                    <Button data-testid="pm-detail-archive-btn" onClick={archive} variant="outline" className="rounded-full text-[#DC2626] border-[#FEF2F2] hover:bg-[#FEF2F2]/40">
                        <Archive size={14} className="mr-1.5" weight="duotone" /> Archive
                    </Button>
                </div>
            )}

            <div className="border-t border-brand-line pt-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1"><ClockClockwise size={12} weight="duotone" /> Occurrence History</p>
                <ul className="space-y-2" data-testid="pm-detail-history">
                    {history.map((h, i) => (
                        <li key={i} className="bg-brand-bg border border-brand-line rounded-sm p-3 flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-brand-ink">
                                    {h.kind === "upcoming" ? "Upcoming occurrence" : (h.work_order_number || "Work Order")}
                                </p>
                                <p className="text-xs text-brand-inkSoft">Due {fmtDate(h.due_date)}{h.completed_at ? ` · Completed ${fmt(h.completed_at)}` : ""}{h.assigned_provider_name ? ` · ${h.assigned_provider_name}` : ""}</p>
                                {h.completion_summary && <p className="text-xs text-brand-inkSoft italic mt-1">"{h.completion_summary}"</p>}
                                {financialLabel(h) && <p className="text-xs text-brand-inkSoft mt-1" data-testid={`pm-history-financial-${i}`}>{financialLabel(h)}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {h.actual_cost != null && <span className="text-xs text-brand-inkSoft">₹{h.actual_cost.toLocaleString("en-IN")}</span>}
                                <Chip variant={h.status === "COMPLETED" || h.status === "CLOSED" ? "success" : (DUE_VARIANT[h.status?.toLowerCase()] || "default")}>{label(h.status)}</Chip>
                                {h.work_order_id && (
                                    <Link to="/app/work-orders" className="text-brand-action hover:underline inline-flex items-center gap-1 text-xs" data-testid={`pm-history-wo-link-${i}`}>
                                        View<ArrowSquareOut size={10} />
                                    </Link>
                                )}
                            </div>
                        </li>
                    ))}
                    {history.length === 0 && <p className="text-xs text-brand-inkSoft italic">No occurrences yet.</p>}
                </ul>
            </div>
        </div>
    );
}

// ---------- Main page ----------
export default function PreventiveMaintenance() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isStaff = user?.role === "admin" || user?.role === "committee";

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [meta, setMeta] = useState({ categories: [], common_areas: [], frequencies: [], priorities: [] });
    const [providers, setProviders] = useState([]);
    const [flats, setFlats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);

    const [q, setQ] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [dueFilter, setDueFilter] = useState("all");

    const [createOpen, setCreateOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [selectedId, setSelectedId] = useState(null);

    async function load() {
        setLoading(true);
        try {
            const params = {};
            if (categoryFilter !== "all") params.category = categoryFilter;
            if (statusFilter !== "all") params.status = statusFilter;
            if (dueFilter !== "all") params.due_status = dueFilter;
            if (q.trim()) params.q = q.trim();
            const [list, sm] = await Promise.all([
                api.get("/preventive-maintenance", { params }),
                api.get("/preventive-maintenance/summary"),
            ]);
            setRows(list.data); setSummary(sm.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [categoryFilter, statusFilter, dueFilter]);

    useEffect(() => {
        if (!isStaff) return;
        api.get("/preventive-maintenance/meta").then(r => setMeta(r.data)).catch(() => { });
        api.get("/staff", { params: { active: "true" } }).then(r => setProviders(r.data || [])).catch(() => { });
        api.get("/flats").then(r => setFlats(r.data || [])).catch(() => { });
    }, [isStaff]);

    // Deep-link from Work Orders: navigate here with state={{ openPlanId }}
    useEffect(() => {
        if (isStaff && location.state?.openPlanId) {
            setSelectedId(location.state.openPlanId);
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff]);

    const blocks = useMemo(() => Array.from(new Set(flats.map(f => f.block).filter(Boolean))).sort(), [flats]);
    const flatsForBlock = useMemo(() => flats.filter(f => f.block === form.target_block_id), [flats, form.target_block_id]);

    function openCreate() {
        setForm(emptyForm());
        setEditId(null);
        setCreateOpen(true);
    }
    function openEdit(p) {
        setForm({
            name: p.name, category: p.category, description: p.description || "",
            target_type: p.target_type, common_area: p.common_area || "", common_area_location: p.common_area_location || "",
            target_block_id: p.target_flat_id ? (flats.find(f => f.id === p.target_flat_id)?.block || "") : "",
            target_flat_id: p.target_flat_id || "",
            frequency: p.frequency, custom_interval_days: p.custom_interval_days || "",
            start_date: p.start_date, default_provider_id: p.default_provider_id || "",
            priority: p.priority, estimated_cost: p.estimated_cost ?? "",
        });
        setEditId(p.id);
        setCreateOpen(true);
    }

    async function submitForm(e) {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Plan name is required"); return; }
        if (!form.category) { toast.error("Category is required"); return; }
        if (form.target_type === "COMMON_AREA" && !form.common_area) { toast.error("Select a common area"); return; }
        if (form.target_type === "RESIDENT" && !form.target_flat_id) { toast.error("Select a flat"); return; }
        if (form.frequency === "custom" && !(form.custom_interval_days && Number(form.custom_interval_days) > 0)) { toast.error("Custom interval (days) is required"); return; }
        const payload = { ...form };
        delete payload.target_block_id;
        payload.custom_interval_days = form.frequency === "custom" ? Number(form.custom_interval_days) : null;
        payload.estimated_cost = payload.estimated_cost ? parseFloat(payload.estimated_cost) : null;
        for (const k of ["common_area", "common_area_location", "target_flat_id", "default_provider_id"]) {
            if (!payload[k]) payload[k] = null;
        }
        try {
            if (editId) {
                await api.put(`/preventive-maintenance/${editId}`, payload);
                toast.success("Plan updated");
            } else {
                await api.post("/preventive-maintenance", payload);
                toast.success("Plan created");
            }
            setCreateOpen(false); load();
        } catch (e) { toast.error(formatError(e)); }
    }

    async function generateDue() {
        setGenerating(true);
        try {
            const r = await api.post("/preventive-maintenance/generate-due");
            toast.success(r.data.message);
            load();
        } catch (e) { toast.error(formatError(e)); }
        finally { setGenerating(false); }
    }

    if (!isStaff) return <EmptyState title="Not available" description="Preventive Maintenance is only available to admin/committee." />;

    return (
        <div className="animate-fade-up space-y-6" data-testid="preventive-maintenance-page">
            <PageHeader
                overline="Operations"
                title="Preventive Maintenance"
                description="Recurring maintenance plans — lifts, generators, fire safety and more. Due occurrences generate a Work Order automatically."
                actions={
                    <>
                        <Button data-testid="pm-generate-due-btn" onClick={generateDue} disabled={generating} variant="outline" className="rounded-full">
                            <Lightning size={16} className="mr-1.5" weight="duotone" /> {generating ? "Generating..." : "Generate Due Work Orders"}
                        </Button>
                        <Button data-testid="pm-create-btn" onClick={openCreate} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                            <Plus size={16} className="mr-1.5" /> New Plan
                        </Button>
                    </>
                }
            />

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pm-summary">
                    <StatCard label="Active Plans" value={summary.active_plans} testId="pm-summary-active" />
                    <StatCard label="Due Today" value={summary.due} accent testId="pm-summary-due" />
                    <StatCard label="Overdue" value={summary.overdue} accent testId="pm-summary-overdue" />
                    <StatCard label="Upcoming" value={summary.upcoming} testId="pm-summary-upcoming" />
                    <StatCard label="Completed This Month" value={summary.completed_this_month} testId="pm-summary-completed" />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="pm-filters">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="pm-search" placeholder="Search plan name..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                        <SelectTrigger data-testid="pm-category-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All categories</SelectItem>
                            {meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="pm-status-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="PAUSED">Paused</SelectItem>
                            <SelectItem value="ARCHIVED">Archived</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={dueFilter} onValueChange={setDueFilter}>
                        <SelectTrigger data-testid="pm-due-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All due states</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="due">Due today</SelectItem>
                            <SelectItem value="due_soon">Due soon</SelectItem>
                            <SelectItem value="upcoming">Upcoming</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={load} className="text-brand-ink">Search</Button>
                    <Button variant="ghost" size="sm" onClick={() => { setQ(""); setCategoryFilter("all"); setStatusFilter("all"); setDueFilter("all"); }} className="text-brand-inkSoft">Clear</Button>
                </div>
            </div>

            {loading ? <LoadingState /> : rows.length === 0 ? (
                <EmptyState title="No preventive maintenance plans yet" description="Create a plan for recurring lift, generator, fire safety or other scheduled maintenance." />
            ) : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="pm-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Plan</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Category</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Target</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Frequency</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Provider</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Status</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Due</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(p => (
                                    <tr key={p.id} data-testid={`pm-row-${p.id}`} onClick={() => setSelectedId(p.id)} className="border-b border-brand-line/60 hover:bg-brand-bg/50 cursor-pointer">
                                        <td className="py-3 px-4 text-brand-ink font-medium">
                                            {p.name}
                                            <button
                                                data-testid={`pm-edit-btn-${p.id}`}
                                                onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                                                className="ml-2 text-[10px] uppercase tracking-overline text-brand-action hover:underline"
                                            >Edit</button>
                                        </td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{p.category}</td>
                                        <td className="py-3 px-4 text-xs">
                                            {p.target_type === "COMMON_AREA" ? <><Buildings size={11} weight="duotone" className="inline mr-1" />{p.common_area}</> : p.target_type === "RESIDENT" ? <><HouseLine size={11} weight="duotone" className="inline mr-1" />{p.target_flat_label}</> : <><Globe size={11} weight="duotone" className="inline mr-1" />General</>}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-brand-inkSoft">{p.frequency === "custom" ? `Every ${p.custom_interval_days}d` : label(p.frequency)}</td>
                                        <td className="py-3 px-4 text-xs text-brand-inkSoft">{p.default_provider_name || "—"}</td>
                                        <td className="py-3 px-4"><Chip variant={STATUS_VARIANT[p.status] || "default"}>{label(p.status)}</Chip></td>
                                        <td className="py-3 px-4"><Chip variant={DUE_VARIANT[p.due_state]}>{dueLabel(p)}</Chip></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create / Edit dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">{editId ? "Edit Plan" : "New Preventive Maintenance Plan"}</DialogTitle>
                        <DialogDescription>{editId ? "Frequency/provider changes apply from the next occurrence onward." : "Define what needs to happen and how often."}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitForm} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label>Plan name *</Label><Input data-testid="pm-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Lift AMC Service" /></div>
                            <div><Label>Category *</Label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="pm-form-category" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick category" /></SelectTrigger>
                                    <SelectContent>{meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label>Description</Label><Textarea data-testid="pm-form-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm border-brand-line" /></div>

                        <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg">
                            <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Target</Label>
                            <div className="flex gap-2">
                                <Button type="button" onClick={() => setForm({ ...form, target_type: "GENERAL" })} data-testid="pm-target-general"
                                    className={`flex-1 rounded-full ${form.target_type === "GENERAL" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><Globe size={14} className="mr-1" />General</Button>
                                <Button type="button" onClick={() => setForm({ ...form, target_type: "COMMON_AREA" })} data-testid="pm-target-common"
                                    className={`flex-1 rounded-full ${form.target_type === "COMMON_AREA" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><Buildings size={14} className="mr-1" />Common Area</Button>
                                <Button type="button" onClick={() => setForm({ ...form, target_type: "RESIDENT" })} data-testid="pm-target-resident"
                                    className={`flex-1 rounded-full ${form.target_type === "RESIDENT" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><HouseLine size={14} className="mr-1" />Resident</Button>
                            </div>
                            {form.target_type === "COMMON_AREA" && (
                                <div className="grid md:grid-cols-2 gap-2 pt-2">
                                    <div><Label className="text-xs">Common Area *</Label>
                                        <Select value={form.common_area} onValueChange={(v) => setForm({ ...form, common_area: v })}>
                                            <SelectTrigger data-testid="pm-form-common-area" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick area" /></SelectTrigger>
                                            <SelectContent>{meta.common_areas.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="text-xs">Location</Label><Input data-testid="pm-form-common-loc" value={form.common_area_location} onChange={(e) => setForm({ ...form, common_area_location: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Tower A" /></div>
                                </div>
                            )}
                            {form.target_type === "RESIDENT" && (
                                <div className="grid md:grid-cols-2 gap-2 pt-2">
                                    <div><Label className="text-xs">Block *</Label>
                                        <Select value={form.target_block_id} onValueChange={(v) => setForm({ ...form, target_block_id: v, target_flat_id: "" })}>
                                            <SelectTrigger data-testid="pm-form-block" className="rounded-sm border-brand-line"><SelectValue placeholder="Block" /></SelectTrigger>
                                            <SelectContent>{blocks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="text-xs">Flat *</Label>
                                        <Select value={form.target_flat_id} onValueChange={(v) => setForm({ ...form, target_flat_id: v })}>
                                            <SelectTrigger data-testid="pm-form-flat" className="rounded-sm border-brand-line"><SelectValue placeholder={form.target_block_id ? "Pick flat" : "Pick block first"} /></SelectTrigger>
                                            <SelectContent>{flatsForBlock.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number || f.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label>Frequency *</Label>
                                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                                    <SelectTrigger data-testid="pm-form-frequency" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{meta.frequencies.map(f => <SelectItem key={f} value={f}>{label(f)}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            {form.frequency === "custom" && (
                                <div><Label>Interval (days) *</Label><Input data-testid="pm-form-custom-days" type="number" min="1" value={form.custom_interval_days} onChange={(e) => setForm({ ...form, custom_interval_days: e.target.value })} className="rounded-sm border-brand-line" /></div>
                            )}
                            <div><Label>Start date *</Label><Input data-testid="pm-form-start-date" type="date" required disabled={!!editId} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm border-brand-line disabled:opacity-60" /></div>
                            <div><Label>Priority</Label>
                                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                    <SelectTrigger data-testid="pm-form-priority" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{label(p)}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Default provider</Label>
                                <Select value={form.default_provider_id} onValueChange={(v) => setForm({ ...form, default_provider_id: v })}>
                                    <SelectTrigger data-testid="pm-form-provider" className="rounded-sm border-brand-line"><SelectValue placeholder="Optional" /></SelectTrigger>
                                    <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.entity_type}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Estimated cost (₹)</Label><Input data-testid="pm-form-est-cost" type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className="rounded-sm border-brand-line" /></div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="pm-form-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">{editId ? "Save Changes" : "Create Plan"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)}>
                <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Plan details</DialogTitle>
                        <DialogDescription>Full plan schedule and occurrence history.</DialogDescription>
                    </DialogHeader>
                    <button onClick={() => setSelectedId(null)} className="absolute right-4 top-4 text-brand-inkSoft hover:text-brand-ink"><X size={18} /></button>
                    {selectedId && <PlanDetail id={selectedId} onClose={() => setSelectedId(null)} onChange={load} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
